import React from "react";
import * as THREE from "three";
import { NIWebGL } from "../runtime/ni-webgl.js";
import { Logo } from "./logo.jsx";
import { S, anchor, openConsult } from "../site.js";
const { useEffect: useEffect_Nav, useState: useState_Nav, useRef: useRef_Nav } = React;

/* ── minimal layered icons (lit from above, never spin) ──────────────── */
const NAV_ICONS = {
  ai: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.5"/><circle cx="5" cy="6" r="1.7" stroke="currentColor" strokeWidth="1.5"/><circle cx="19" cy="7" r="1.7" stroke="currentColor" strokeWidth="1.5"/><circle cx="18" cy="18" r="1.7" stroke="currentColor" strokeWidth="1.5"/><path d="M6.4 7.1l3.4 3.2M16.9 8.3l-2.4 1.9M16.7 16.7l-2.3-2.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
  ),
  mvp: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5"/><rect x="7.5" y="7.5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M11 11h2v2h-2z" fill="currentColor"/></svg>
  ),
  web: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 9h18" stroke="currentColor" strokeWidth="1.3"/><circle cx="5.6" cy="7" r=".7" fill="currentColor"/><path d="M9 18v2M15 18v2M7.5 20h9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
  ),
  aiReady: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5"/><path d="M4 12h16M12 4c2.6 2.2 2.6 13.8 0 16M12 4c-2.6 2.2-2.6 13.8 0 16" stroke="currentColor" strokeWidth="1.2"/></svg>
  ),
  smartpost: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3.5l1.9 4.4 4.6.5-3.5 3 1 4.6L12 13.8 7.5 16l1-4.6-3.5-3 4.6-.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/></svg>
  ),
  talent: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="8" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.5"/><circle cx="16" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.5"/><path d="M4 18c0-2.4 1.9-3.8 4-3.8s4 1.4 4 3.8M12 18c0-2.4 1.9-3.8 4-3.8s4 1.4 4 3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
  ),
};

const Chevron = () => (
  <svg className="chev" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
);
const ArrowGo = ({ cls }) => (
  <svg className={cls} width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h9M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

function LinkCard({ to, icon, vermilion, title, desc, tags, onNav }) {
  return (
    <a className="lcard" href={to} onClick={onNav}>
      <span className={"chip" + (vermilion ? " is-vermilion" : "")} aria-hidden="true">{icon}</span>
      <span>
        <span className="lcard-t">{title}<ArrowGo cls="go" /></span>
        <p className="lcard-d">{desc}</p>
        {tags && (
          <span className="lcard-tags">
            {tags.map((t, i) => <span key={i} className={"tag" + (t[0] ? " tag-" + t[0] : "")}>{t[1]}</span>)}
          </span>
        )}
      </span>
    </a>
  );
}

/* ── calm, on-ink 3D scenes for the mega panels ───────────────────────
   One <MegaScene> per panel. Cool indigo + warm-paper accents on the
   dark feature-rail; slow, never-spinning motion to match the editorial
   tone. Only the active panel runs its animation loop; everything
   disposes on unmount.                                                  */
const SCENE_COL = {
  indigo: 0x7c87e8,   // brighter than --indigo so it reads on ink
  verm:   0xe06a43,   // --vermilion, lifted for the dark canvas
  paper:  0xf3efe6,   // warm white
};

/* Solutions → an orchestration hub wiring four service nodes together */
function buildSolutions(root, c) {
  const hub = c.track(new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.34, 1),
    new THREE.MeshBasicMaterial({ color: c.indigo, wireframe: true, transparent: true, opacity: 0.9 })
  ));
  root.add(hub);
  const lineMat = new THREE.LineBasicMaterial({ color: c.paper, transparent: true, opacity: 0.22 });
  const sats = [];
  const N = 4;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2;
    const r = 1.7;
    const p = new THREE.Vector3(Math.cos(a) * r, Math.sin(a * 1.3) * 0.55, Math.sin(a) * r);
    const dot = c.track(new THREE.Mesh(
      new THREE.SphereGeometry(0.13, 18, 18),
      new THREE.MeshBasicMaterial({ color: i === 0 ? c.indigo : c.paper, transparent: true, opacity: 0.9 })
    ));
    dot.position.copy(p);
    const line = c.track(new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), p]), lineMat));
    root.add(dot); root.add(line);
    sats.push({ dot, i });
  }
  c.disposables.push(lineMat);
  root.rotation.x = 0.18;
  return (t) => {
    root.rotation.y = t * 0.16;
    hub.rotation.y = -t * 0.4; hub.rotation.x = t * 0.22;
    sats.forEach((s) => s.dot.scale.setScalar(1 + Math.sin(t * 1.6 + s.i) * 0.14));
  };
}

/* Products → three product surfaces floating in space (SmartPost + TalentCloud) */
function buildProducts(root, c) {
  const defs = [
    { col: c.verm,   x: -0.95, y: 0.32, z: 0.45, rz: -0.30, w: 1.5, h: 1.02, op: 0.16 },
    { col: c.indigo, x: 0.95,  y: -0.30, z: -0.15, rz: 0.26, w: 1.42, h: 0.96, op: 0.16 },
    { col: c.paper,  x: 0.05,  y: 0.05, z: 0.95, rz: 0.05, w: 1.18, h: 0.78, op: 0.08 },
  ];
  const cards = [];
  defs.forEach((d, i) => {
    const plane = new THREE.PlaneGeometry(d.w, d.h);
    const face = c.track(new THREE.Mesh(plane, new THREE.MeshBasicMaterial({ color: d.col, transparent: true, opacity: d.op, side: THREE.DoubleSide })));
    const edge = c.track(new THREE.LineSegments(new THREE.EdgesGeometry(plane), new THREE.LineBasicMaterial({ color: d.col, transparent: true, opacity: 0.7 })));
    const g = new THREE.Group();
    g.add(face); g.add(edge);
    // a couple of "content rows" so each surface reads as a product card
    for (let r = 0; r < 2; r++) {
      const row = c.track(new THREE.Mesh(
        new THREE.PlaneGeometry(d.w * (0.62 - r * 0.18), 0.05),
        new THREE.MeshBasicMaterial({ color: c.paper, transparent: true, opacity: 0.34 })
      ));
      row.position.set(-d.w * 0.12, d.h * 0.22 - r * 0.2, 0.01);
      g.add(row);
    }
    g.position.set(d.x, d.y, d.z); g.rotation.z = d.rz; g.rotation.y = d.rz * 0.5;
    root.add(g);
    cards.push({ g, base: d, i });
  });
  root.rotation.x = 0.12;
  return (t) => {
    root.rotation.y = Math.sin(t * 0.25) * 0.26;
    cards.forEach((o) => {
      o.g.position.y = o.base.y + Math.sin(t * 0.9 + o.i * 1.6) * 0.12;
      o.g.rotation.x = Math.sin(t * 0.6 + o.i) * 0.07;
    });
  };
}

const SCENE_BUILDERS = { solutions: buildSolutions, products: buildProducts };

function MegaScene({ kind, active }) {
  const holder = useRef_Nav(null);
  const store = useRef_Nav(null);
  const [failed, setFailed] = useState_Nav(false);

  // build once per kind
  useEffect_Nav(() => {
    const safety = NIWebGL;
    if (!holder.current || !SCENE_BUILDERS[kind]) {
      setFailed(true);
      return undefined;
    }
    const el = holder.current;
    let renderer, scene, ro, lifecycle, loop, unwatchContext;
    try {
      const rendererResult = safety.createRenderer(
        { alpha: true, antialias: true },
        `nav-mega-${kind}`,
      );
      if (!rendererResult.ok) {
        setFailed(true);
        return undefined;
      }
      renderer = rendererResult.renderer;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0x000000, 0);
      const cv = renderer.domElement;
      cv.style.width = "100%"; cv.style.height = "100%"; cv.style.display = "block";
      el.appendChild(cv);

      scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
      camera.position.set(0, 0, 5);
      const root = new THREE.Group();
      scene.add(root);

      const disposables = [];
      const track = (o) => { if (o.geometry) disposables.push(o.geometry); if (o.material) disposables.push(o.material); return o; };
      const update = SCENE_BUILDERS[kind](root, { ...SCENE_COL, track, disposables });
      const renderStatic = () => {
        update(0.6);
        renderer.render(scene, camera);
      };
      const resize = () => {
        const w = el.clientWidth || 320, h = el.clientHeight || 170;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h; camera.updateProjectionMatrix();
        renderer.render(scene, camera);
      };
      resize();
      ro = new ResizeObserver(resize); ro.observe(el);
      lifecycle = safety.createVisualLifecycle(el, {
        onFailure: () => setFailed(true),
        onStateChange: (state) => {
          if (state.reducedMotion && renderer) renderStatic();
        },
      });
      unwatchContext = safety.watchContextLoss(cv, lifecycle, () => {
        safety.disposeThreeTree(scene);
        safety.disposeRenderer(renderer);
      });
      if (lifecycle.state.reducedMotion) renderStatic();
      const state = { t: 0 };
      loop = safety.createSafeAnimationLoop({
        name: `nav-mega-${kind}`,
        lifecycle,
        enabled: false,
        step: () => {
          state.t += 0.016;
          update(state.t);
          renderer.render(scene, camera);
        },
        onError: () => setFailed(true),
      });

      store.current = { renderer, scene, ro, lifecycle, loop, unwatchContext };
      return () => {
        const current = store.current;
        store.current = null;
        current?.loop.dispose();
        current?.unwatchContext();
        current?.lifecycle.dispose();
        current?.ro.disconnect();
        safety.disposeThreeTree(current?.scene);
        safety.disposeRenderer(current?.renderer);
      };
    } catch (error) {
      setFailed(true);
      loop?.dispose();
      unwatchContext?.();
      lifecycle?.dispose();
      ro?.disconnect();
      safety.disposeThreeTree(scene);
      safety.disposeRenderer(renderer);
      return undefined;
    }
  }, [kind]);

  // run the loop only while this panel is the open one
  useEffect_Nav(() => {
    const s = store.current;
    if (!s) return undefined;
    s.loop.setEnabled(active);
    return () => s.loop.setEnabled(false);
  }, [active]);

  return (
    <div className="mega3d" ref={holder} aria-hidden="true">
      {failed ? <NIWebGL.VisualFallback /> : null}
    </div>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState_Nav(false);
  const [hidden, setHidden] = useState_Nav(false);
  const lastY = useRef_Nav(0);
  const [openMega, setOpenMega] = useState_Nav(null);   // 'solutions' | 'products' | null
  const [renderMega, setRenderMega] = useState_Nav(null);
  const [drawer, setDrawer] = useState_Nav(false);
  const [acc, setAcc] = useState_Nav({});               // mobile accordion open-state
  const navRef = useRef_Nav(null);
  const hoverCloseTimer = useRef_Nav(null);

  // Every entry is a section on this page. The individual service and product
  // pages these used to link to are gone, so each one now points at the section
  // that actually covers it.
  const R = {
    solutionsAll: S.services,
    ai: S.ai,
    digitalProductSystem: S.digitalProductSystem,
    mvp: S.mvp,
    web: S.web,
    aiReady: S.website,
    aiMarketing: anchor(S.aiMarketing),
    talentTeam: anchor(S.workforce),
    productsAll: S.products,
    smartpost: S.smartpost,
    nextCx: S.nextCx,
    talentcloud: S.talentcloud,
    nextWorkforce: S.nextWorkforce,
    marketSupport: anchor(S.marketSupport),
    works: S.works,
    company: S.about,
    contact: S.contact,
  };

  // surface frosts when scrolled or a panel is open;
  // bar hides on scroll down, returns on scroll up
  useEffect_Nav(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const dy = y - lastY.current;
      // ponytail: 6px dead-zone kills mobile rubber-band jitter
      if (y < 80) setHidden(false);
      else if (dy > 6) setHidden(true);
      else if (dy < -6) setHidden(false);
      lastY.current = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // keep last panel content mounted through the fade-out
  useEffect_Nav(() => { if (openMega) setRenderMega(openMega); }, [openMega]);

  // escape closes everything; lock scroll while the drawer is open
  useEffect_Nav(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenMega(null); setDrawer(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect_Nav(() => {
    if (!drawer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawer]);

  // The consultation launcher is fixed above the page. Keep it out of the
  // way while a navigation layer is open, particularly on desktop where the
  // launcher otherwise sits above the mega menu.
  useEffect_Nav(() => {
    const navigationOpen = Boolean(openMega || drawer);
    document.body.classList.toggle("nx-nav-open", navigationOpen);
    return () => document.body.classList.remove("nx-nav-open");
  }, [openMega, drawer]);

  const clearHoverClose = () => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  };
  const toggleMega = (id) => {
    clearHoverClose();
    setOpenMega((cur) => (cur === id ? null : id));
  };
  const closeMega = () => {
    clearHoverClose();
    setOpenMega(null);
  };
  const toggleAcc = (id) => setAcc((s) => ({ ...s, [id]: !s[id] }));
  const openConsultPanel = openConsult({ source: "nav" });
  const startConsultation = (event) => {
    closeMega();
    setDrawer(false);
    openConsultPanel(event);
  };

  const frosted = scrolled || openMega;

  const trigger = (id, label) => (
    <button
      className="nav-item"
      type="button"
      aria-haspopup="true"
      aria-controls="desktop-mega-menu"
      aria-expanded={openMega === id}
      onClick={() => toggleMega(id)}
    >
      {label}<Chevron />
    </button>
  );

  return (
    <React.Fragment>
      <header
        ref={navRef}
        className={"nav" + (frosted ? " is-scrolled" : "") + (openMega ? " is-open" : "") + (hidden && !openMega && !drawer ? " is-hidden" : "")}
      >
        <div className="container nav-inner">
          <a href="/" className="logo" aria-label="NEXT INNOVATIONS — home" onClick={closeMega}>
            <Logo />
          </a>

          <nav className="nav-links" aria-label="Primary">
            {trigger("solutions", "Solutions")}
            {trigger("products", "Products")}
            <a className="nav-item" href={R.works} onClick={closeMega}>Works</a>
            <a className="nav-item" href={R.marketSupport} onClick={closeMega}>Myanmar Market Support</a>
            <a className="nav-item" href={R.company} onClick={closeMega}>About</a>
          </nav>

          <div className="nav-right">
            <a className="btn btn-primary" href={R.contact} onClick={startConsultation}>
              <span>Start a consultation</span><ArrowGo />
            </a>
          </div>

          <button
            className="hamburger"
            type="button"
            aria-label="Open menu"
            aria-expanded={drawer}
            onClick={() => setDrawer(true)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 7h18M3 12h18M3 17h18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
          </button>
        </div>
      </header>

      {/* ── mega panels ─────────────────────────────────────────────── */}
      <div className={"scrim" + (openMega ? " show" : "")} onClick={closeMega} />
      <div className="mega-wrap">
        <div id="desktop-mega-menu" className={"mega" + (openMega ? " show" : "")} role="region" aria-label={(renderMega || "") + " menu"}>
          {renderMega === "solutions" && (
            <React.Fragment>
              <div className="mega-head">
                <div>
                  <p className="eyebrow">How we help</p>
                  <p>From AI transformation to product, customer experience, systems, and team delivery.</p>
                </div>
                <a className="mega-allink" href={R.solutionsAll} onClick={closeMega}>All solutions<ArrowGo /></a>
              </div>
              <div className="mega-grid mega-solutions">
                <div className="solution-groups">
                  <a className="solution-parent-link" href={R.digitalProductSystem} onClick={closeMega}>
                    <span className="chip" aria-hidden="true">{NAV_ICONS.web}</span>
                    <span>
                      <span className="solution-parent-kicker">Solution overview</span>
                      <span className="lcard-t">AI Transformation to Digital Delivery<ArrowGo cls="go" /></span>
                      <span className="solution-parent-copy">Connect the operating change, product direction, and digital delivery needed to move forward.</span>
                    </span>
                  </a>
                  <div className="solution-group">
                    <p className="product-group-label">Solution areas</p>
                    <div className="col-cards cards-3">
                      <LinkCard to={R.ai} icon={NAV_ICONS.ai} title="AI Business Solutions" desc="Change operations, sales, and customer response with AI." tags={[["indigo", "AI transformation"]]} onNav={closeMega} />
                      <LinkCard to={R.mvp} icon={NAV_ICONS.mvp} title="Product / MVP Development" desc="Test what the market will choose, then grow the product." tags={[["", "Product direction"]]} onNav={closeMega} />
                      <LinkCard to={R.web} icon={NAV_ICONS.web} title="Web System & App Development" desc="Build the right system, SaaS, or app — with or without AI." tags={[["", "Digital delivery"]]} onNav={closeMega} />
                      <LinkCard to={R.aiMarketing} icon={NAV_ICONS.smartpost} title="AI Marketing & Customer Experience" desc="Improve customer understanding, conversations, and growth." tags={[["indigo", "Customer growth"]]} onNav={closeMega} />
                      <LinkCard to={R.aiReady} icon={NAV_ICONS.aiReady} title="AI-Ready Website" desc="Make your website easier to discover, understand, and contact." tags={[["", "Website"]]} onNav={closeMega} />
                      <LinkCard to={R.talentTeam} icon={NAV_ICONS.talent} title="Talent & Team Solutions" desc="Shape the talent, team, and delivery model around the work." tags={[["", "Team delivery"]]} onNav={closeMega} />
                    </div>
                  </div>
                </div>
                <aside className="feature-rail">
                  <p className="eyebrow">Start with the outcome</p>
                  <h4>Choose the right change, then <span className="nav-emphasis">deliver it well.</span></h4>
                  <p>We help identify whether the next move is AI, a product, a customer experience, a system, or a team model.</p>
                  <div className="mega-brief" aria-hidden="true"><span>01</span><span>Clarify the outcome</span><span>02</span><span>Choose the route</span><span>03</span><span>Deliver with ownership</span></div>
                  <a className="btn btn-on-ink" href={R.contact} onClick={startConsultation}><span>Start a consultation</span><ArrowGo /></a>
                </aside>
              </div>
            </React.Fragment>
          )}

          {renderMega === "products" && (
            <React.Fragment>
              <div className="mega-head">
                <div>
                  <p className="eyebrow">Connected Product Ecosystem</p>
                  <p>Four PoC / Beta products across customer growth and workforce growth.</p>
                </div>
                <a className="mega-allink" href={R.productsAll} onClick={closeMega}>All products<ArrowGo /></a>
              </div>
              <div className="mega-grid mega-products">
                <div className="product-groups">
                  <div className="product-group">
                    <p className="product-group-label">Customer Growth</p>
                    <div className="col-cards cards-2">
                      <LinkCard to={R.smartpost} icon={NAV_ICONS.smartpost} vermilion title="SmartPost AI" desc="Reach and content workflows for pilot teams." tags={[["vermilion", "PoC / Beta"]]} onNav={closeMega} />
                      <LinkCard to={R.nextCx} icon={NAV_ICONS.ai} title="NEXT CX AI" desc="AI consultation, lead capture, and human handover." tags={[["indigo", "PoC / Beta"]]} onNav={closeMega} />
                    </div>
                  </div>
                  <div className="product-group">
                    <p className="product-group-label">Workforce Growth</p>
                    <div className="col-cards cards-2">
                      <LinkCard to={R.talentcloud} icon={NAV_ICONS.talent} title="Talent Cloud" desc="Talent discovery and hiring workflows for pilots." tags={[["indigo", "PoC / Beta"]]} onNav={closeMega} />
                      <LinkCard to={R.nextWorkforce} icon={NAV_ICONS.mvp} title="NEXT Workforce" desc="Projects and tasks across human and AI team members." tags={[["", "PoC / Beta"]]} onNav={closeMega} />
                    </div>
                  </div>
                </div>
                <aside className="feature-rail">
                  <p className="eyebrow">Product ecosystem</p>
                  <h4>Products shaped around <span className="nav-emphasis">real operations.</span></h4>
                  <p>Explore customer-growth and workforce products currently available for a focused pilot or rollout.</p>
                  <div className="mega-brief" aria-hidden="true"><span>01</span><span>Customer growth</span><span>02</span><span>Workforce growth</span><span>03</span><span>Focused pilot</span></div>
                  <a className="btn btn-on-ink" href={R.productsAll} onClick={closeMega}><span>Explore Products</span><ArrowGo /></a>
                </aside>
              </div>
            </React.Fragment>
          )}

        </div>
      </div>

      {/* ── mobile drawer (full-screen editorial) ───────────────────── */}
      <div className={"drawer" + (drawer ? " open" : "")} role="dialog" aria-modal="true" aria-label="Menu" aria-hidden={!drawer}>
        <div className="container drawer-top">
          <button className="drawer-x" type="button" aria-label="Close menu" onClick={() => setDrawer(false)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
          </button>
        </div>
        <div className="container drawer-body">
          <div className={"dacc" + (acc.solutions ? " open" : "")}>
            <button className="dacc-head" type="button" aria-expanded={!!acc.solutions} onClick={() => toggleAcc("solutions")}>
              Solutions
              <svg className="dchev" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="dacc-panel"><div className="dsub">
              <a className="dsub-all" href={R.solutionsAll} onClick={() => setDrawer(false)}>All solutions
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
              <span className="dsub-group-label">Solution overview</span>
              <a href={R.digitalProductSystem} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.web}</span><span className="dprimary">AI Transformation to Digital Delivery</span></a>
              <span className="dsub-group-label">Solution areas</span>
              <a href={R.ai} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.ai}</span>AI Business Solutions</a>
              <a href={R.mvp} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.mvp}</span>Product / MVP Development</a>
              <a href={R.web} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.web}</span>Web System & App Development</a>
              <a href={R.aiMarketing} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.smartpost}</span>AI Marketing &amp; Customer Experience</a>
              <a href={R.aiReady} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.aiReady}</span>AI-Ready Website</a>
              <a href={R.talentTeam} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.talent}</span>Talent &amp; Team Solutions</a>
            </div></div>
          </div>

          <div className={"dacc" + (acc.products ? " open" : "")}>
            <button className="dacc-head" type="button" aria-expanded={!!acc.products} onClick={() => toggleAcc("products")}>
              Products
              <svg className="dchev" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <div className="dacc-panel"><div className="dsub">
              <a className="dsub-all" href={R.productsAll} onClick={() => setDrawer(false)}>All products
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
              <span className="dsub-group-label">Customer Growth</span>
              <a href={R.smartpost} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.smartpost}</span>SmartPost AI</a>
              <a href={R.nextCx} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.ai}</span>NEXT CX AI</a>
              <span className="dsub-group-label">Workforce Growth</span>
              <a href={R.talentcloud} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.talent}</span>Talent Cloud</a>
              <a href={R.nextWorkforce} onClick={() => setDrawer(false)}><span className="dmark">{NAV_ICONS.mvp}</span>NEXT Workforce</a>
            </div></div>
          </div>

          <a className="dlink" href={R.works} onClick={() => setDrawer(false)}>Works
            <svg className="dgo" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>
          <a className="dlink dlink-market" href={R.marketSupport} onClick={() => setDrawer(false)}>Myanmar Market Support
            <svg className="dgo" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>
          <a className="dlink" href={R.company} onClick={() => setDrawer(false)}>About
            <svg className="dgo" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>

          <div className="drawer-cta">
            <a className="btn btn-primary" href={R.contact} onClick={startConsultation}><span>Start a consultation</span><ArrowGo /></a>
          </div>
          <p className="drawer-meta">Yangon · Tokyo — replies within 1 business day</p>
        </div>
      </div>
    </React.Fragment>
  );
}

export { Nav };
